/**
 * File: useDateRangePickerController.ts
 * Path: apps/admin/src/features/dashboard/hooks/useDateRangePickerController.ts
 * Description: The analysis range picker's open/shut state, the draft start date, the chosen preset, and
 * the actions that change them. The end date is always locked to today.
 */

import { useEffect, useRef, useState } from "react";
import { isoDay, presetStartDay, type DateRange } from "@/features/dashboard/utils/dateRangeUtils";

export const useDateRangePickerController = (value: DateRange, onChange: (range: DateRange) => void) => {
  const [isOpen, setIsOpen] = useState(false);

  // We only need to track the draft "Start Date" now, as "End Date" is always today
  const [draftStartDate, setDraftStartDate] = useState<string>(value.startDate);
  const [activePreset, setActivePreset] = useState<string>("30");

  const containerRef = useRef<HTMLDivElement>(null);

  // Constant anchor for today's date
  const today = new Date();
  const todayStr = isoDay(today);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- re-seeds the draft whenever the picker opens or the committed value changes while open; moving it to the open handler drops the second case (lint-baseline note)
      setDraftStartDate(value.startDate);
    }
  }, [isOpen, value.startDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const applyPreset = (days: number, presetLabel: string) => {
    setActivePreset(presetLabel);
    setDraftStartDate(presetStartDay(days));
  };

  // When a user clicks a date on the calendar, we interpret it as the new START date
  const selectCalendarDay = (selectedDay: Date) => {
    if (!selectedDay) return;
    setActivePreset("custom");
    setDraftStartDate(isoDay(selectedDay));
  };

  const apply = () => {
    onChange({
      startDate: draftStartDate,
      endDate: todayStr, // Always lock the end date to today on commit
    });
    setIsOpen(false);
  };

  return {
    isOpen,
    today,
    containerRef,
    activePreset,
    selectedRange: { from: draftStartDate ? new Date(draftStartDate) : undefined, to: today },
    toggle: () => setIsOpen(!isOpen),
    close: () => setIsOpen(false),
    applyPreset,
    selectCalendarDay,
    apply,
  };
};
